/**
 * @file RepFlagResolveModal.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-28
 * @modified 2026-11-01
 * @description "Dealt with…" on a rep's flag in Rep Pricing (Cam, 28 Oct 2026
 *              — asked how the office marks a flag dealt with: "Allow user to
 *              select from searchable list of customer's documents but ensure
 *              each option shows a clear short summary of the document
 *              otherwise they will have to pick from memory.").
 *
 *              The flag as the rep wrote it, then a searchable list of THAT
 *              customer's documents (GET /api/pricing-costing/rep-flags/:id/
 *              documents?q): the order itself, its invoices, credit and debit
 *              notes, overpayments, and their contracts for the product — each
 *              option a short summary (utils/repPricingView.documentSummary:
 *              type and number; date, total, what is outstanding, what it was
 *              raised for, the order it came from; the products on it), the
 *              line's own invoice and anything raised for it marked and first.
 *              The search goes to the server (every word). Choosing one is
 *              optional; a note is optional. Before migration
 *              rep_price_flag_resolution_documents.sql the list is greyed with
 *              the reason and a note alone marks it dealt with.
 *
 *              Props: { flag, onClose, onDone(body) }.
 *
 *              1.1.0 (2026-11-01): the same window marks an invoice's
 *              "Needs correcting" flag dealt with (InvoiceCorrectionPanel) —
 *              optional props `documentsUrl` (the picker's list),
 *              `submit(document, note)` (the save, answering the server's
 *              body), `title`, `heading` (what is being dealt with, in place
 *              of the rep's flag) and `customerName`.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import Modal from './Modal';
import Textarea from './Textarea';
import SearchableDropdown from './SearchableDropdown';
import { useSlideOutButton } from './SlideOutButton';
import { API_URL } from '../config';
import { money, ukDateFull } from '../utils/pricingCosting';
import { documentSummary, documentValue, documentOf } from '../utils/repPricingView';
import './RepPricingView.css';

function RepFlagResolveModal({ flag, onClose, onDone, documentsUrl = null, submit: submitOverride = null, title = null, heading = null, customerName = null }) {
  const [docs, setDocs] = useState([]);
  const [more, setMore] = useState(0);
  const [ready, setReady] = useState(true);
  const [notReady, setNotReady] = useState(null);
  const [loading, setLoading] = useState(true);
  const [term, setTerm] = useState('');
  const [chosen, setChosen] = useState('');
  const [chosenDoc, setChosenDoc] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const seq = useRef(0);

  useEffect(() => {
    const ticket = ++seq.current;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const q = new URLSearchParams(term.trim() ? { q: term.trim() } : {});
        const res = await fetch(`${documentsUrl || `${API_URL}/api/pricing-costing/rep-flags/${flag.id}/documents`}?${q}`, { credentials: 'include' });
        const body = await res.json();
        if (ticket !== seq.current) return;
        if (!res.ok || !body.success) throw new Error(body.error || 'The documents could not be read');
        setDocs(body.documents || []);
        setMore(body.more || 0);
        setReady(body.ready !== false);
        setNotReady(body.not_ready_reason || null);
      } catch (err) {
        if (ticket === seq.current) setError(err.message);
      } finally {
        if (ticket === seq.current) setLoading(false);
      }
    }, term ? 300 : 0);
    return () => clearTimeout(t);
  }, [term, flag.id, documentsUrl]);

  // The option chosen stays listed whatever the search shows now.
  const options = useMemo(() => {
    const list = chosenDoc && !docs.some(d => documentValue(d) === documentValue(chosenDoc)) ? [chosenDoc, ...docs] : docs;
    return list.map(d => {
      const s = documentSummary(d);
      return {
        value: documentValue(d),
        label: s.title,
        searchText: s.searchText,
        renderLabel: (
          <span className="rp-doc-option">
            <span className="rp-doc-title">{s.title}{s.relation && <span className="rp-doc-rel">{s.relation}</span>}</span>
            {s.meta && <span className="rp-doc-meta">{s.meta}</span>}
            {s.products && <span className="rp-doc-products">{s.products}</span>}
          </span>
        )
      };
    });
  }, [docs, chosenDoc]);

  const choose = (value) => {
    setChosen(value);
    setChosenDoc(documentOf(value, [...docs, ...(chosenDoc ? [chosenDoc] : [])]));
  };

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const document = chosenDoc ? { type: chosenDoc.type, id: chosenDoc.id } : null;
      if (submitOverride) {
        onDone(await submitOverride(document, note.trim() || null));
        return;
      }
      const res = await fetch(`${API_URL}/api/pricing-costing/rep-flags/resolve`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ ids: [flag.id], note: note.trim() || null, ...(document ? { document } : {}) })
      });
      const body = await res.json();
      if (!res.ok || !body.success) throw new Error(body.error || 'Not marked dealt with');
      const refused = (body.results || []).find(r => r.outcome !== 'resolved');
      if (refused) throw new Error(refused.message || 'Not marked dealt with');
      onDone(body);
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  const footer = (
    <div className="buttons slideout">
      <div>
        <button type="button" className="btn-slideout" onClick={onClose} disabled={busy}>
          <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
          <span className="btn-label">Cancel</span>
        </button>
      </div>
      <div>
        <button type="button" className="btn-slideout" onClick={submit} disabled={busy}
          title={chosenDoc ? `Mark it dealt with — settled by ${documentSummary(chosenDoc).title}` : 'Mark it dealt with (no document chosen)'}>
          <div className="icon-wrapper"><span className="material-symbols-rounded">task_alt</span></div>
          <span className="btn-label">{busy ? 'Saving…' : 'Mark dealt with'}</span>
        </button>
      </div>
    </div>
  );

  const line = flag.line || {};
  return (
    <Modal isOpen onClose={busy ? undefined : onClose} title={title || "Rep's flag — dealt with"} size="lg" className="rp-resolve" footer={footer}>
      <ResolveBody busy={busy} chosen={chosen}>
        {error && <p className="rp-note err"><span className="material-symbols-rounded">error</span>{error}</p>}
        {heading || (
        <div className="rp-resolve-flag">
          <span className="material-symbols-rounded rp-flag-icon">flag</span>
          <div>
            <div><b>{line.customer?.account_no ? `${line.customer.account_no} ` : ''}{line.customer?.name}</b> · {line.product?.name || line.product?.code}</div>
            <div className="rp-muted">
              {line.order_number}{line.delivery_date ? ` · delivery ${ukDateFull(line.delivery_date)}` : ''}
              {flag.invoice?.invoice_no ? ` · invoiced ${flag.invoice.invoice_no}` : ''}
              {flag.sale_price !== null && flag.sale_price !== undefined ? ` · ${money(flag.sale_price)} when flagged` : ''}
            </div>
            <div className="rp-flag-note">“{flag.note}”</div>
            <div className="rp-muted">{flag.kind === 'pull_back' ? 'Pulled back to price again by ' : 'Flagged by '}{flag.by || flag.rep_name || 'the rep'}{flag.at ? ` · ${ukDateFull(new Date(flag.at).toISOString())}` : ''}</div>
          </div>
        </div>
        )}
        <div className="filter-group rp-resolve-doc">
          <label>Settled by (optional) — {customerName || line.customer?.name}'s documents</label>
          <SearchableDropdown
            options={options}
            value={chosen}
            onChange={choose}
            placeholder={ready ? 'Choose the invoice, credit note, order or contract that settled it…' : (notReady || 'Not ready')}
            searchPlaceholder="Search by number, product, order or amount"
            disabled={!ready || busy}
            onSearchChange={setTerm}
            serverFiltered
            loading={loading}
            listNote={more > 0 ? `Showing the newest ${docs.filter(d => d.type !== 'order' && d.type !== 'contract').length} invoices and credit notes — ${more} more; type to narrow` : null}
            escapeOverflow
          />
          {!ready && <p className="rp-muted">{notReady} — mark it dealt with with a note instead.</p>}
          {chosenDoc && (() => {
            const s = documentSummary(chosenDoc);
            return (
              <div className="rp-doc-chosen">
                <span className="rp-doc-title">{s.title}{s.relation && <span className="rp-doc-rel">{s.relation}</span>}</span>
                <span className="rp-doc-meta">{s.meta}</span>
                {s.products && <span className="rp-doc-products">{s.products}</span>}
                <button type="button" className="rp-link" onClick={() => { setChosen(''); setChosenDoc(null); }}>Clear</button>
              </div>
            );
          })()}
        </div>
        <div className="filter-group">
          <label>Note (optional)</label>
          <Textarea value={note} rows={3} maxLength={1000} placeholder="What was done — e.g. credited £10/T, rang the customer"
            onChange={(e) => setNote(e.target.value)} />
        </div>
      </ResolveBody>
    </Modal>
  );
}

function ResolveBody({ busy, chosen, children }) {
  useSlideOutButton([busy, chosen]);
  return <div className="rp-resolve-body">{children}</div>;
}

export default RepFlagResolveModal;
